import React, { useId } from 'react';

/**
 * Statbox branded inline loader. The mark is three nested rounded squares:
 * the outer and inner squares are filled with the brand gradient
 * (#2A3FB8 → #2BB5A0) and pulse in sync; the middle square is filled with
 * the surface color (via `currentColor`) so it reads as a transparent
 * "ring hole" against whatever surface the loader sits on. Surface color
 * is resolved by the wrapper's `text-white dark:text-slate-900` so the
 * middle layer disappears in both light and dark mode without inversion.
 *
 * Props:
 *   size      (number, default 24)  — width/height in px
 *   label     (string, optional)    — text rendered after the mark
 *   className (string, optional)    — pass through to the wrapper span
 */
const Loader = ({ size = 24, label, className = '' }) => {
  // Unique gradient id per instance so multiple loaders on the same page
  // don't share/clobber each other's <linearGradient>.
  const gradientId = `statbox-loader-${useId().replace(/:/g, '')}`;

  return (
    <span
      className={`inline-flex items-center text-current ${className}`}
      style={{ gap: '8px' }}
      role="status"
      aria-live="polite"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 120 120"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2A3FB8" />
            <stop offset="100%" stopColor="#2BB5A0" />
          </linearGradient>
        </defs>

        {/* Outer filled square (animated, in sync with inner) */}
        <rect
          x="10"
          y="15"
          width="100"
          height="90"
          rx="18"
          fill={`url(#${gradientId})`}
          opacity="0.25"
        >
          <animate
            attributeName="opacity"
            values="0.25;1;1;0.25"
            keyTimes="0;0.4;0.55;1"
            dur="2s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.4 0 0.2 1; 0 0 1 1; 0.4 0 0.2 1"
          />
        </rect>

        {/* Middle "hole" rect — matches surface color via currentColor, static */}
        <rect
          x="35"
          y="38"
          width="50"
          height="44"
          rx="6"
          fill="currentColor"
        />

        {/* Inner filled square (same animation timing as outer — synced) */}
        <rect
          x="52"
          y="52"
          width="16"
          height="16"
          rx="2"
          fill={`url(#${gradientId})`}
          opacity="0.25"
        >
          <animate
            attributeName="opacity"
            values="0.25;1;1;0.25"
            keyTimes="0;0.4;0.55;1"
            dur="2s"
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.4 0 0.2 1; 0 0 1 1; 0.4 0 0.2 1"
          />
        </rect>
      </svg>
      {label && (
        <span
          style={{
            fontSize: '13px',
            color: 'var(--color-text-secondary, #64748b)',
          }}
        >
          {label}
        </span>
      )}
      {!label && <span className="sr-only">Loading</span>}
    </span>
  );
};

export default Loader;
